/**
 * Single source of truth for the application's role → permission model.
 *
 * This consolidates what were previously THREE hand-maintained copies of
 * getDefaultPermissions (src/firebase.ts, src/contexts/AuthContext.tsx, and a
 * divergent one in src/types/userTypes.ts). The copies had drifted: Manager and
 * HR were granted `processPayroll: true` in one file and `false` in another, so
 * whether a Manager could run payroll depended on which module a code path
 * happened to read. Centralizing here makes that impossible.
 *
 * POLICY (decided 2026-06-10): payroll processing is restricted to Owner,
 * Administrator, and Accountant. Manager and HR can VIEW payroll but cannot run
 * it. Owner is not in this table — Owner is granted everything by the isOwner
 * override in AuthContext.hasPermission().
 *
 * NOTE: src/types/userTypes.ts defines a SEPARATE, more granular 26-field
 * Permissions model (viewSensitiveEmployeeData, time-tracking, etc.) used only by
 * Navigation (for the UserRole type) and debug tooling. That model is intentionally
 * left untouched here; reconciling the two permission systems is a product decision
 * tracked as a follow-up, not a behavior-preserving refactor.
 */

export type UserRole =
  | 'Owner'
  | 'Administrator'
  | 'Manager'
  | 'HR'
  | 'Employee'
  | 'Accountant'
  | 'Guest';

export interface Permissions {
  viewEmployees: boolean;
  manageEmployees: boolean;
  viewPayroll: boolean;
  processPayroll: boolean;
  viewReports: boolean;
  manageReports: boolean;
  viewCompanies: boolean;
  manageCompanies: boolean;
  viewSettings: boolean;
  manageSettings: boolean;
  manageTaxSettings: boolean;
  manageUsers: boolean;
}

/** Everything denied. The baseline every role is built up from. */
export const NO_PERMISSIONS: Readonly<Permissions> = Object.freeze({
  viewEmployees: false,
  manageEmployees: false,
  viewPayroll: false,
  processPayroll: false,
  viewReports: false,
  manageReports: false,
  viewCompanies: false,
  manageCompanies: false,
  viewSettings: false,
  manageSettings: false,
  manageTaxSettings: false,
  manageUsers: false,
});

/**
 * Per-role grants, expressed as the deltas from NO_PERMISSIONS. Any key not
 * listed for a role stays denied. Roles not present here (Owner, Guest) resolve
 * to NO_PERMISSIONS — Owner is elevated separately via the isOwner override.
 */
const ROLE_GRANTS: Partial<Record<UserRole, Partial<Permissions>>> = {
  Administrator: {
    viewEmployees: true,
    manageEmployees: true,
    viewPayroll: true,
    processPayroll: true,
    viewReports: true,
    manageReports: true,
    viewCompanies: true,
    manageCompanies: true,
    viewSettings: true,
    manageSettings: true,
    manageTaxSettings: true,
    manageUsers: true,
  },
  Manager: {
    viewEmployees: true,
    manageEmployees: true,
    viewPayroll: true,
    // processPayroll intentionally denied — Managers cannot run payroll.
    viewReports: true,
    viewCompanies: true,
    viewSettings: true,
  },
  HR: {
    viewEmployees: true,
    manageEmployees: true,
    viewPayroll: true,
    // processPayroll intentionally denied — HR cannot run payroll.
    viewReports: true,
  },
  Accountant: {
    viewEmployees: true,
    viewPayroll: true,
    processPayroll: true,
    viewReports: true,
    manageReports: true,
    viewCompanies: true,
    manageTaxSettings: true,
  },
  // Employee, Owner, Guest → NO_PERMISSIONS (Owner elevated via isOwner override).
};

/** Default permission set for a role. Always returns a fresh, complete object. */
export const getDefaultPermissions = (role: UserRole): Permissions => ({
  ...NO_PERMISSIONS,
  ...(ROLE_GRANTS[role] ?? {}),
});
